import { EBool } from '~/src/dash/api/types/bool';
import { ISimpleOrder } from '~/src/dash/apiV2/campaigns';
import {
  generateAdvertiserOptionsList,
  generateTraffickerOptionsList,
} from '~/src/dash/apiV2/campaigns/filters';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { generateOptionsListAsPromise } from '~/src/dash/components/dropdowns/common';
import {
  convertSetIntoArrayOfNumbers,
  convertSetIntoSingleNumber,
} from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import {
  CampaignStatusOptions,
  CampaignStatusPresets,
} from '~/src/dash/components/filters/options/campaigns';
import { EStatus, StatusOptions } from '~/src/dash/components/filters/status';
import {
  IListViewTableColumns,
  IListViewTablePagination,
  IListViewTableRow,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  DateTimeRenderUnchanged,
  EllipsisWithTooltipRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { CampaignStatusRender } from '~/src/dash/views/campaigns/orders/OrdersListView/common';

export interface IOrdersListViewConfig {
  cypressClassName: string;
  startToEndDateRanges: IStartToEndDateRanges;
  timezone: string;
  filters: Record<string, IExtendedIFilterRowObject>;
  columns: IListViewTableColumns;
  table: IListViewTablePagination;
  activeColumnKeys: string[];
  columnSelector: {
    resetKeys: string[];
  };
}

export const ordersListViewConfig: IOrdersListViewConfig = {
  cypressClassName: 'lineitemslistview',
  startToEndDateRanges: {
    startDateFrom: null,
    startDateTo: null,
    endDateFrom: null,
    endDateTo: null,
  },
  timezone: '',
  filters: {
    Status: {
      key: 'Status',
      tKey: 'Status',
      getList: () => generateOptionsListAsPromise(StatusOptions),
      selectedLimit: 1,
      isSelectionRequired: true,
      selectedOptionIDs: new Set([EStatus.Active]),
      disableSearch: true,
      disableSort: true,
      displayLabelWithSelection: true,
      selectedOptionIDsOnReset: new Set([EStatus.ShowAll]), // Show All
      queryKey: 'active',
      formatQueryValue: convertSetIntoSingleNumber,
    },
    Advertisers: {
      key: 'Advertisers',
      tKey: 'Advertisers',
      getList: generateAdvertiserOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'advertiser_ids',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    LineItemStatus: {
      key: 'LineItemStatus',
      tKey: 'LineItemStatus',
      getList: () => generateOptionsListAsPromise(CampaignStatusOptions),
      disableSearch: true,
      disableSort: true,
      queryKey: 'status',
      formatQueryValue: convertSetIntoArrayOfNumbers,
      presets: CampaignStatusPresets,
    },
    Traffickers: {
      key: 'Traffickers',
      tKey: 'Traffickers',
      getList: generateTraffickerOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'trafficker_ids',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
  },
  table: {
    page: 0,
    rowsPerPage: 10,
    order: 'asc',
    orderBy: 'start_date',
  },
  activeColumnKeys: ['LineItemStatus', 'StartDate', 'EndDate', 'Advertiser', 'Impressions'],
  columnSelector: {
    resetKeys: ['LineItemStatus', 'StartDate', 'EndDate', 'Advertiser', 'Impressions'],
  },
  columns: {
    ID: {
      formatValue: (value: number) => value.toString(),
      isHidden: true,
      isSearchable: true,
      key: 'ID',
      label: 'ID',
      requestKey: 'ID',
      responseKey: 'ID',
      valueType: 'string',
    },
    Status: {
      defaultOrderDirection: 'desc',
      formatValue: (value: number) => value === (EBool.EBool_TRUE as number),
      headerRenderType: 'switch',
      key: 'Status',
      tKey: 'Active',
      label: 'Active',
      requestKey: 'active',
      responseKey: 'Active',
      valueType: 'boolean',
      orderByKey: 'active',
    },
    Name: {
      cellJSX: EllipsisWithTooltipRender,
      cellRenderType: 'name_link',
      defaultOrderDirection: 'asc',
      headerRenderType: 'name',
      isSearchable: true,
      key: 'Name',
      label: 'Name',
      tKey: 'Name',
      requestKey: 'name',
      responseKey: 'Name',
      valueType: 'string',
      orderByKey: 'name',
    },
    LineItemStatus: {
      cellJSX: CampaignStatusRender,
      cellRenderType: 'string',
      valueType: 'object',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'LineItemStatus',
      label: 'Line Item Status',
      tKey: 'LineItemStatus',
      requestKey: 'status',
      responseKey: 'CampaignStatuses',
      disableSort: true,
    },
    StartDate: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'StartDate',
      label: 'Start Date',
      tKey: 'StartDate',
      orderByKey: 'start_date',
      requestKey: '',
      responseKey: 'StartDate',
      valueType: 'date',
    },
    EndDate: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'EndDate',
      label: 'End Date',
      tKey: 'EndDate',
      orderByKey: 'end_date',
      requestKey: '',
      responseKey: 'EndDate',
      valueType: 'date',
    },
    Advertiser: {
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Advertiser',
      label: 'Advertiser',
      tKey: 'Advertiser',
      orderByKey: 'advertiser_name',
      requestKey: '',
      responseKey: 'AdvertiserName',
      valueType: 'string',
    },
    Impressions: {
      cellRenderType: 'large_number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Impressions',
      label: 'Delivered Impressions',
      tKey: 'DeliveredImpressions',
      orderByKey: 'impressions',
      requestKey: '',
      responseKey: 'Impressions',
      valueType: 'number',
    },
    Actions: {
      cellRenderType: 'actions',
      disableSort: true,
      headerRenderType: 'actions',
      key: 'Actions',
      label: 'Actions',
      tKey: 'Actions',
      requestKey: '',
      responseKey: '',
      valueType: 'component',
    },
    Trafficker: {
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Trafficker',
      label: 'Trafficker',
      tKey: 'Trafficker',
      orderByKey: 'trafficker_name',
      requestKey: '',
      responseKey: 'TraffickerName',
      valueType: 'string',
    },
    NumCampaigns: {
      cellRenderType: 'large_number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'NumCampaigns',
      label: 'Line Items Total',
      tKey: 'LineItemsTotal',
      orderByKey: 'num_campaigns',
      requestKey: '',
      responseKey: 'NumCampaigns',
      valueType: 'number',
    },
  },
};

export const formatOrdersResponseIntoLVTRows = (
  orders: ISimpleOrder[],
  cfg: IListViewTableColumns,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  orders.forEach((order) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: unknown = order[responseKey as keyof ISimpleOrder];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};
